import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../app/store";
import { getAccountDetailByAccountNumber } from "../../creditAccounts/logic/slice";
import { updateCreditAccountDetails } from "../logic/accountDetailsThunk";

import SidepeekDetailsAccordion from "../../../components/ui/accordion/v2/SidepeekDetailsAccordion";
import EditableField, {OptionNameValuePair} from "../../../components/ui/formelement/v2/EditableField";
import ConfirmModal from "../../../components/ui/modals/ConfirmModal";

import {BRANCH_CATEGORY_LABELS, BRANCH_SIZE_LABELS} from "../../../api/branchDetails";

import { showErrorToast } from "../../../lib/toast";
import { getBranchDetailByBranchId } from "../../branchDetails/logic/branchDetailsSlice";
import {fetchBranchDetails, upsertBranchDetails} from "../../branchDetails/logic/branchDetailsThunks";

interface BranchDetailsProps {
  accountNumber: string;
}

const BRANCH_CATEGORY_OPTIONS: OptionNameValuePair[] = Object.entries(
  BRANCH_CATEGORY_LABELS,
).map(([value, name]) => ({
  name,
  value,
}));

const BRANCH_SIZE_OPTIONS: OptionNameValuePair[] = Object.entries(
  BRANCH_SIZE_LABELS,
).map(([value, name]) => ({
  name,
  value,
}));

const BranchDetails: React.FC<BranchDetailsProps> = ({ accountNumber }) => {
  const { id: caseId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();

  const [editingField, setEditingField] = useState<string | null>(null);
  const [pendingValue, setPendingValue] = useState<any>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const accountDetails = useAppSelector((state) =>
    getAccountDetailByAccountNumber(state, accountNumber),
  );
  const branchId = accountDetails?.accountOverview?.credit_account_details?.branch_id;
  const branch = useAppSelector((state) =>
    getBranchDetailByBranchId(state, branchId ?? undefined),
  );

  useEffect(() => {
    if (!branchId || branch) return;

    setIsLoading(true);
    dispatch(fetchBranchDetails([branchId]))
      .then((action) => {
        if (fetchBranchDetails.rejected.match(action) && !action.meta.condition) {
          showErrorToast("Failed to fetch branch details.");
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [branchId, branch, dispatch]);

  const isDisabled = !branchId || isLoading || isSaving;
  const disabledMessage = !branchId
    ? "Please enter branch ID"
    : isLoading
      ? "Loading branch details..."
      : undefined;

  const handleSaveBranchId = async (val: string | null) => {
    if (!caseId) return false;
    setIsSaving(true);
    try {
      await dispatch(
        updateCreditAccountDetails({
          caseId,
          accountNumber,
          request: { branch_id: val?.trim() || null },
        }),
      ).unwrap();
      setEditingField(null);
      return true;
    } catch {
      showErrorToast("Failed to update Branch ID.");
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleInitiateSave = async (val: any) => {
    setPendingValue(val ?? "");
    return true;
  };

  const handleConfirmSave = async () => {
    if (!branchId || !editingField) return;
    setIsSaving(true);
    try {
      await dispatch(
        upsertBranchDetails({
          ...branch,
          branch_id: branchId,
          [editingField]: pendingValue,
        }),
      ).unwrap();

      setEditingField(null);
      setPendingValue(null);
    } catch {
      showErrorToast("Failed to update branch details.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <SidepeekDetailsAccordion title="Branch Details">
        <div className="flex w-full flex-col gap-2">
          <EditableField
            label="Branch ID"
            value={branchId ?? null}
            type="text"
            isEditable
            isEditing={editingField === "branch_id"}
            onEditStart={() => setEditingField("branch_id")}
            onEditCancel={() => setEditingField(null)}
            onSave={handleSaveBranchId}
            disabled={isSaving || isLoading}
          />

          <EditableField
            label="Zonal Office"
            value={branch?.zonal_office ?? null}
            type="text"
            isLoading={isLoading}
            isEditable
            isEditing={editingField === "zonal_office"}
            onEditStart={() => setEditingField("zonal_office")}
            onEditCancel={() => setEditingField(null)}
            onSave={handleInitiateSave}
            disabled={isDisabled}
            disabledMessage={disabledMessage}
          />

          <EditableField
            label="Branch Name"
            value={branch?.name ?? null}
            type="text"
            isLoading={isLoading}
            isEditable
            isEditing={editingField === "name"}
            onEditStart={() => setEditingField("name")}
            onEditCancel={() => setEditingField(null)}
            onSave={handleInitiateSave}
            disabled={isDisabled}
            disabledMessage={disabledMessage}
          />

          <EditableField
            label="SOL ID"
            value={branch?.sol_id ?? null}
            type="text"
            isLoading={isLoading}
            isEditable
            isEditing={editingField === "sol_id"}
            onEditStart={() => setEditingField("sol_id")}
            onEditCancel={() => setEditingField(null)}
            onSave={handleInitiateSave}
            disabled={isDisabled}
            disabledMessage={disabledMessage}
          />

          <EditableField
            label="Branch Category"
            value={branch?.category ?? null}
            type="select"
            isLoading={isLoading}
            options={BRANCH_CATEGORY_OPTIONS}
            displayValue={branch?.category ? BRANCH_CATEGORY_LABELS[branch.category]: "--"}
            isEditable
            isEditing={editingField === "category"}
            onEditStart={() => setEditingField("category")}
            onEditCancel={() => setEditingField(null)}
            onSave={handleInitiateSave}
            disabled={isDisabled}
            disabledMessage={disabledMessage}
          />

          <EditableField
            label="Branch Size"
            value={branch?.size ?? null}
            type="select"
            isLoading={isLoading}
            options={BRANCH_SIZE_OPTIONS}
            displayValue={branch?.size ? BRANCH_SIZE_LABELS[branch.size] : "--"}
            isEditable
            isEditing={editingField === "size"}
            onEditStart={() => setEditingField("size")}
            onEditCancel={() => setEditingField(null)}
            onSave={handleInitiateSave}
            disabled={isDisabled}
            disabledMessage={disabledMessage}
          />

          <EditableField
            label="Branch Address"
            value={branch?.address ?? null}
            type="textarea"
            isLoading={isLoading}
            isEditable
            isEditing={editingField === "address"}
            onEditStart={() => setEditingField("address")}
            onEditCancel={() => setEditingField(null)}
            onSave={handleInitiateSave}
            disabled={isDisabled}
            disabledMessage={disabledMessage}
          />
        </div>
      </SidepeekDetailsAccordion>

      {pendingValue !== null &&
        createPortal(
          <ConfirmModal
            title="Confirm Changes"
            description="If you make the changes, it will change branch information across organisation."
            isLoading={isSaving}
            onConfirm={handleConfirmSave}
            onCancel={() => setPendingValue(null)}
          />,
          document.body,
        )}
    </>
  );
};

export default BranchDetails;